<script setup>
import { onMounted, onUnmounted } from 'vue'
import { useStore } from 'vuex'

import Button from '@/components/Button.vue'
import TopBar from '@/components/layout/TopBar.vue'
import { useAgentNotices } from '@/composables/useAgentNotices'
import { useNewRelease } from '@/composables/useNewRelease'
import { useUnreadAlerts } from '@/composables/useUnreadAlerts'

const store = useStore()

// Both live here so they keep working while the operator is in monitoring
useUnreadAlerts()
const { current: notice, dismiss: dismissNotice } = useAgentNotices()
const { available: newRelease, reload } = useNewRelease()

// Polled from the shell so the badge and the chime work on every page
const STATS_INTERVAL = 5000
let statsTimer = null

const pollStats = async () => {
  try {
    await store.dispatch('stats/fetch')
  } catch (error) {
    // non-fatal, the next round retries
  }
  // Scheduled after the answer, so slow responses cannot pile up
  statsTimer = setTimeout(pollStats, STATS_INTERVAL)
}

// Coming back to the tab should show the current counters at once
const onTabVisible = () => {
  if (document.hidden) return

  clearTimeout(statsTimer)
  pollStats()
}

onMounted(() => {
  if (!store.getters['auth/getCurrentUser']) {
    store.dispatch('auth/validityCheck')
  }
  pollStats()
  document.addEventListener('visibilitychange', onTabVisible)
})

onUnmounted(() => {
  clearTimeout(statsTimer)
  document.removeEventListener('visibilitychange', onTabVisible)
})
</script>

<template>
  <div class="flex flex-col w-full h-screen bg-n-background">
    <TopBar />
    <div class="flex flex-col flex-1 min-h-0 overflow-hidden">
      <router-view />
    </div>

    <!-- Bottom of the screen: nothing is broken yet, so nothing is blocked -->
    <div
      v-if="newRelease"
      class="fixed z-40 flex items-center gap-3 px-4 py-3 -translate-x-1/2 rounded-xl bottom-4 left-1/2 bg-n-solid-1 shadow-lg border border-n-weak"
    >
      <span class="i-lucide-refresh-cw size-4 text-n-slate-11" />
      <p class="m-0 text-body-main text-n-slate-12">
        {{ $t('RELEASE.AVAILABLE') }}
      </p>
      <Button sm :label="$t('RELEASE.RELOAD')" @click="reload" />
    </div>

    <!-- A supervisor's push: middle of the screen, dismissed by hand -->
    <div
      v-if="notice"
      class="fixed inset-0 z-50 grid place-content-center bg-black/40 px-4"
    >
      <div
        class="flex flex-col w-full max-w-md gap-4 p-6 rounded-2xl bg-n-solid-1 shadow-lg"
      >
        <span class="flex items-center gap-2 text-xs text-n-amber-11">
          <span class="i-lucide-megaphone size-4" />
          {{ $t('NOTICE.FROM', { name: notice.sender_name }) }}
        </span>
        <p class="m-0 whitespace-pre-wrap text-heading-3 text-n-slate-12">
          {{ notice.content }}
        </p>
        <div class="flex justify-end">
          <Button :label="$t('NOTICE.GOT_IT')" @click="dismissNotice" />
        </div>
      </div>
    </div>
  </div>
</template>
